import { chakra, mergeRefs } from '@chakra-ui/react';
import { DragControls, motion, useDragControls, useMotionValue } from 'framer-motion';
import {
  ComponentProps,
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
interface Props extends Omit<ComponentProps<typeof MotionBox>, 'children'> {
  top: number;
  left: number;
  constraintsRef?: RefObject<HTMLDivElement | null>;
}

export const MotionBox = chakra(motion.div);
export interface DragContextValue {
  dragControls: DragControls;
  isDragging: boolean;
}

export const DragControlsContext = createContext<DragContextValue | null>(null);

export const useDragControlsContext = (): DragContextValue => {
  const context = useContext(DragControlsContext);
  if (!context) {
    throw new Error('useDragControlsContext must be used within a DragControlsContext.Provider');
  }
  return context;
};
export function DraggableBox({ constraintsRef, top, left, children, ref, ...boxProps }: PropsWithChildren<Props>) {
  const dragControls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef<number | null>(null);
  const x = useMotionValue(left);
  const y = useMotionValue(top);

  const contextValue = useMemo(() => ({ dragControls, isDragging, setIsDragging }), [dragControls, isDragging]);

  const handleDragStart = () => {
    setIsDragging(true);
    document.body.style.userSelect = 'none';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    document.body.style.userSelect = '';
  };

  const correctIfOverflowing = useCallback(() => {
    if (!boxRef.current || !constraintsRef?.current) return;

    const boxRect = boxRef.current.getBoundingClientRect();
    const containerRect = constraintsRef.current.getBoundingClientRect();

    let deltaX = 0;
    let deltaY = 0;

    if (boxRect.right > containerRect.right) {
      deltaX = containerRect.right - boxRect.right;
    }

    if (boxRect.left < containerRect.left) {
      deltaX = containerRect.left - boxRect.left;
    }

    if (boxRect.bottom > containerRect.bottom) {
      deltaY = containerRect.bottom - boxRect.bottom;
    }

    if (boxRect.top < containerRect.top) {
      deltaY = containerRect.top - boxRect.top;
    }

    if (deltaX !== 0) {
      x.set(x.get() + deltaX);
    }

    if (deltaY !== 0) {
      y.set(y.get() + deltaY);
    }
  }, [constraintsRef, x, y]);

  const scheduleCorrection = useCallback(() => {
    if (lockRef.current !== null) return;

    lockRef.current = requestAnimationFrame(() => {
      lockRef.current = null;
      correctIfOverflowing();
    });
  }, [correctIfOverflowing]);

  useLayoutEffect(() => {
    if (!boxRef.current || !constraintsRef?.current) return;

    const observer = new ResizeObserver(() => {
      scheduleCorrection();
    });

    observer.observe(boxRef.current);
    observer.observe(constraintsRef.current);

    return () => {
      observer.disconnect();
      if (lockRef.current !== null) {
        cancelAnimationFrame(lockRef.current);
        lockRef.current = null;
      }
    };
  }, [constraintsRef, correctIfOverflowing, scheduleCorrection]);

  return (
    <MotionBox
      drag
      dragListener={false}
      dragControls={dragControls}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      dragElastic={0}
      dragMomentum={false}
      style={{ x, y }}
      {...(constraintsRef ? { dragConstraints: constraintsRef } : {})}
      {...boxProps}
      ref={mergeRefs(ref, boxRef)}
    >
      <DragControlsContext.Provider value={contextValue}>{children}</DragControlsContext.Provider>
    </MotionBox>
  );
}
